import Big from "big.js";
import { BookingAddOn } from "../AppointmentBookingContext/useBookingAddOns";
import { Coupon } from "../AppointmentBookingContext/types";

export type SummaryPositionAddOn = {
  id?: string;
  name: string;
  price: number;
  quantity?: number;
};
export type SummaryPositionCenterService = {
  name: string;
  price: number;
  depositAmount: number;
  studyIds: string[];
  addOnIds: string[];
  addOnQuantities?: Record<string, number>;
};

const getTotalSummary = (params: {
  centerServices: SummaryPositionCenterService[];
  addOns: SummaryPositionAddOn[];
  centerAddOns: BookingAddOn[];
  coupon: Coupon | null;
  discountAmount?: number;
  overriddenPrice: number | null;
  overriddenDeposit: number | null;
}) => {
  const {
    centerServices,
    addOns,
    discountAmount,
    overriddenPrice,
    overriddenDeposit,
    centerAddOns,
    coupon,
  } = params;

  const centerServicesPriceBig = centerServices.reduce((sum, centerService) => {
    const studiesCount = centerService.studyIds.length;
    const centerServiceAddonsPrices = centerService.addOnIds.reduce(
      (addOnSum, addOnId) => {
        const addOnPrice =
          centerAddOns.find((centerAddOn) => centerAddOn.id === addOnId)
            ?.price || 0;
        const quantity = centerService.addOnQuantities?.[addOnId] ?? 1;

        const addOnPriceBig = Big(addOnPrice)
          .times(studiesCount)
          .times(quantity);
        return addOnSum.plus(addOnPriceBig);
      },
      Big(0),
    );

    return sum.plus(centerService.price).plus(centerServiceAddonsPrices);
  }, Big(0));

  const addOnsPriceBig = addOns.reduce(
    (sum, addOn) => sum.plus(addOn.price),
    Big(0),
  );

  const price = centerServicesPriceBig.plus(addOnsPriceBig).round(2).toNumber();

  const additionalServicesLength = Math.max(centerServices.length - 1, 0);

  const servicesDiscountSum = discountAmount
    ? Big(discountAmount).times(additionalServicesLength).toNumber()
    : 0;

  const couponAddOnQuantity = (() => {
    if (!coupon?.addOnId) return 1;

    const flatQuantities = addOns
      .filter((addOn) => addOn.id === coupon.addOnId)
      .map((addOn) => addOn.quantity ?? 1);

    const serviceQuantities = centerServices
      .map((service) => service.addOnQuantities?.[coupon.addOnId as string])
      .filter((quantity): quantity is number => !!quantity);

    const quantities = [...flatQuantities, ...serviceQuantities];

    return quantities.length ? Math.min(...quantities) : 1;
  })();

  const liveCouponAmount = coupon?.addOnId
    ? (coupon.addOnPrice ?? 0) * couponAddOnQuantity
    : coupon?.amount || 0;

  const totalDiscountSum = Big(liveCouponAmount || 0).plus(servicesDiscountSum);

  const discountSum = totalDiscountSum.gt(price)
    ? price
    : totalDiscountSum.toNumber();

  const priceWithDiscount = Big(price).minus(discountSum).toNumber();

  const isOverriddenPriceError =
    overriddenPrice !== null &&
    !Number.isNaN(overriddenPrice) &&
    overriddenPrice !== 0 &&
    Big(overriddenPrice).gte(priceWithDiscount);

  const isOverriddenDepositError =
    overriddenDeposit !== null &&
    !Number.isNaN(overriddenDeposit) &&
    (Big(overriddenDeposit).lt(0) ||
      Big(overriddenDeposit).gt(priceWithDiscount) ||
      (typeof overriddenPrice === "number" &&
        Big(overriddenDeposit).gt(overriddenPrice)));

  const servicesDepositAmount = centerServices
    .reduce(
      (sum, centerService) => sum.plus(centerService.depositAmount),
      Big(0),
    )
    .round(2)
    .toNumber();

  const finalPrice =
    overriddenPrice === null ||
    Number.isNaN(overriddenPrice) ||
    isOverriddenPriceError
      ? Big(price).minus(discountSum).toNumber()
      : overriddenPrice;

  const depositAmount =
    overriddenPrice === null ||
    Number.isNaN(overriddenPrice) ||
    isOverriddenPriceError
      ? Math.min(servicesDepositAmount, finalPrice)
      : Math.min(servicesDepositAmount, overriddenPrice);

  return {
    depositAmount:
      typeof overriddenDeposit === "number" && !isOverriddenDepositError
        ? overriddenDeposit
        : depositAmount,
    price,
    discountSum: servicesDiscountSum,
    couponAmount: liveCouponAmount,
    priceWithDiscount: finalPrice,
    isOverriddenPriceError,
    isOverriddenDepositError,
  };
};

export default getTotalSummary;
